import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import {customerSchema, userSchema} from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as userRolesActionCreators from '../user_roles/actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { User } from './model'
import { UserRole } from '../user_roles/model'
import {Customer} from "../customers/model";

// Fetch

const fetchUsersEpic = (action$, store) => {
    const tableIdentifier = 'users_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `users/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(userSchema)
                        )

                        const users = normalizedEntitiesToRecordMap(normalizedData.entities.users, User, normalizedData.result)
                        const userRoles = normalizedEntitiesToRecordMap(normalizedData.entities.user_roles, UserRole)

                        const observables = [
                            Observable.of(actionCreators.fetchUsersFulfilled(users)),
                            Observable.of(userRolesActionCreators.fetchUserRolesFulfilled(userRoles)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchUsersRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchUserEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().users.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `users/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, userSchema)

                        const users = normalizedEntitiesToRecordMap(normalizedData.entities.users, User)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchUsersFulfilled(users)),
                            Observable.of(actionCreators.fetchUserFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchUsersRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveUserEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().users.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)

            let path = `users`
            let method = 'POST'

            if (values.id) {
                path = `users/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, userSchema)

                        const users = normalizedEntitiesToRecordMap(normalizedData.entities.users, User)
                        const user = users.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveUserFulfilled(user)))
                    })
                    .catch(error => Observable.of(actionCreators.saveUserRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteUserEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().users.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `users/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteUserFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteUserRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )


// Restore

const restoreUserEpic = (action$, store) =>
    action$
        .ofType(actionTypes.RESTORE)
        .filter(() => !store.getState().customers.getIn(['restoring', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.RESTORE_STARTED,
                }),
                request({
                    path: `users/${action.payload}/restore`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, userSchema)
                        const items = normalizedEntitiesToRecordMap(normalizedData.entities.users, User)
                        const item = items.valueSeq().first()
                        return Observable.concat(Observable.of(actionCreators.restoreUserFulfilled(item)))
                    })
                    .catch(error => Observable.of(actionCreators.restoreUserRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.RESTORE_CANCELLED))
            )
        )


// Export

const exportUsersEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `users/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

// History

const fetchUserLoginLogEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_LOGIN_LOG)
        .filter(() => !store.getState().users.getIn(['loginLog', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_LOGIN_LOG_STARTED,
                }),
                request({
                    path: `users/${action.payload}/login-log`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchUserLoginLogFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchUserLoginLogRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_LOGIN_LOG_CANCELLED))
            )
        )

const exportUserLoginLogEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_LOGIN_LOG).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `users/${action.payload}/login-log/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_LOGIN_LOG_FULFILLED,
            })
        )
    })

export default [fetchUsersEpic, fetchUserEpic, saveUserEpic, deleteUserEpic, restoreUserEpic, exportUsersEpic, fetchUserLoginLogEpic, exportUserLoginLogEpic]
